import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { AlgoliaCardIndexesName } from '@sorare/core/src/contexts/config';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';

import type { useSortSecondaryOfferSlugs_anyCard } from './__generated__/useSortSecondaryOfferSlugs.graphql';

type Props = {
  slugs: string[];
  anyCards: useSortSecondaryOfferSlugs_anyCard[];
  cardIndex: AlgoliaCardIndexesName;
};

export const useSortSecondaryOfferSlugs = ({
  slugs,
  anyCards = [],
  cardIndex,
}: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();

  const sortedSlugs = [...slugs]?.sort((a, b) => {
    const cardA = anyCards.find(card => card.slug === a);
    const cardB = anyCards.find(card => card.slug === b);

    if (cardA?.liveSingleSaleOffer && cardB?.liveSingleSaleOffer) {
      const priceA = toMonetaryAmount(
        cardA.liveSingleSaleOffer.receiverSide.amounts
      );
      const priceB = toMonetaryAmount(
        cardB.liveSingleSaleOffer.receiverSide.amounts
      );

      if (cardIndex === 'Cards On Sale Lowest Price') {
        return priceA.eurCents - priceB.eurCents;
      }
      if (cardIndex === 'Cards On Sale Highest Price') {
        return priceB.eurCents - priceA.eurCents;
      }
    }
    return 0;
  });
  return sortedSlugs;
};

useSortSecondaryOfferSlugs.fragments = {
  anyCard: gql`
    fragment useSortSecondaryOfferSlugs_anyCard on AnyCardInterface {
      slug
      liveSingleSaleOffer {
        id
        receiverSide {
          id
          amounts {
            ...useMonetaryAmount_monetaryAmount
          }
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<useSortSecondaryOfferSlugs_anyCard>,
};
